import React, { useState, useEffect } from "react";
import { getApiUrl } from "../config/api";
import { ArticleIcon } from "./components/AdminIcons";
import Login from "./components/Login";
import Signup from "./components/Signup";
import Sidebar from "./components/Sidebar";
import Dashboard from "./pages/Dashboard";
import Users from "./pages/Users";
import CrudManager from "./components/CrudManager";

export default function App() {
  const [user, setUser] = useState(null);
  const [currentPage, setCurrentPage] = useState("dashboard");
  const [notification, setNotification] = useState(null);
  const [showSignup, setShowSignup] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  const [articles, setArticles] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [stats, setStats] = useState({
    articles: 0,
    videos: 0,
    shorts: 0,
    epapers: 0,
  });

  // Store the original public title to revert to on logout
  const [originalTitle, setOriginalTitle] = useState("");
  useEffect(() => {
    setOriginalTitle(document.title);
  }, []);

  const handleLogout = (isUnauthorized = false) => {
    setUser(null);
    setCurrentPage("dashboard");
    localStorage.removeItem("adminUser");
    localStorage.removeItem("adminToken");
    const message = isUnauthorized
      ? "Your session has expired. Please log in again."
      : "You have been logged out.";
    setNotification({ message, type: isUnauthorized ? "error" : "success" });
  };

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("adminUser");
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
    } catch (error) {
      console.error("Failed to parse stored user:", error);
      localStorage.removeItem("adminUser");
    }
    // If adminUser exists but no token, expire session
    const token = localStorage.getItem("adminToken");
    if (localStorage.getItem("adminUser") && !token) {
      handleLogout(true);
    }
    setIsAuthLoading(false);
  }, []);

  // Listen for storage events (other tabs)
  useEffect(() => {
    const onStorage = (e) => {
      try {
        if (e.key === "adminToken") {
          const currentToken = localStorage.getItem("adminToken");
          if (!currentToken) {
            handleLogout(true);
          }
        }
        if (e.key === "adminUser") {
          const newVal = e.newValue;
          if (!newVal) {
            handleLogout(true);
            return;
          }
          const parsed = JSON.parse(newVal);
          if (!user || (parsed && parsed.id !== user.id)) {
            handleLogout(true);
          }
        }
      } catch (err) {
        console.error('Storage event handling failed', err);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [user]);

  // Fetch admin users and articles
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("adminToken");
        if (!token) {
          console.warn("No token found.");
          return;
        }

        const usersUrl = getApiUrl('GET_ADMIN_USERS');
        const usersRes = await fetch(usersUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (usersRes.ok) {
          const usersData = await usersRes.json();
          console.log('Admin users fetched:', usersData);
          setAllUsers(usersData.users || []);
        } else {
          console.error('Failed to fetch admin users:', usersRes.status);
        }

        const articlesUrl = getApiUrl('GET_NEWS');
        const articlesRes = await fetch(articlesUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (articlesRes.ok) {
          const articlesData = await articlesRes.json();
          const fetchedArticles = articlesData.posts || [];
          setArticles(fetchedArticles);

          const totalArticles = fetchedArticles.length;
          const totalVideos = fetchedArticles.filter((a) => a.youtubeUrl).length;
          const totalShorts = fetchedArticles.filter((a) => a.shortVideo || a.shortsVideo).length;
          const totalEpapers = fetchedArticles.filter((a) => a.pdf).length;

          setStats({
            articles: totalArticles,
            videos: totalVideos,
            shorts: totalShorts,
            epapers: totalEpapers,
          });
        } else {
          console.error('Failed to fetch articles:', articlesRes.status);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setNotification({
          type: "error",
          message: "Failed to load data. Please refresh the page.",
        });
      }
    };

    if (user && user.isAdmin) {
      fetchData();
    }
  }, [user]);

  // Fetch all users for the articles table
  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (user && user.isAdmin && token) {
      fetch(getApiUrl('GET_USERS'), {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => {
          if ([401, 403].includes(res.status)) {
            handleLogout(true);
            throw new Error("Session expired");
          }
          if (!res.ok) {
            throw new Error(`HTTP error! Status: ${res.status}`);
          }
          return res.json();
        })
        .then((data) => {
          const users = data.users || [];
          try {
            const stored = localStorage.getItem('adminUser');
            if (stored) {
              const parsed = JSON.parse(stored);
              const found = users.find((u) => u.id === parsed.id || u.email === parsed.email);
              if (!found || !found.isAdmin) {
                handleLogout(true);
                return;
              }
            }
          } catch (err) {
            console.error('User validation after GET_USERS failed', err);
          }
        })
        .catch((err) => {
          console.error("Error fetching users:", err.message);
          if (err.message !== "Session expired") {
            setNotification({
              message: "Failed to fetch users.",
              type: "error",
            });
          }
        });
    }
  }, [user]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Meta tags and title management for admin pages
  useEffect(() => {
    const titleSuffix =
      " | Ahmedabad Post: \u0A85\u0AAE\u0AA6\u0ABE\u0AB5\u0ABE\u0AA6\u0AA8\u0ABE \u0AA4\u0ABE\u0A9C\u0ABE \u0A97\u0AC1\u0A9C\u0AB0\u0ABE\u0AA4\u0AC0 \u0AB8\u0AAE\u0ABE\u0A9A\u0ABE\u0AB0, \u0A97\u0AC1\u0A9C\u0AB0\u0ABE\u0AA4 \u0A85\u0AA8\u0AC7 \u0AA6\u0AC7\u0AB6-\u0AA6\u0AC1\u0AA8\u0ABF\u0AAF\u0ABE Live \u0AB8\u0AAE\u0ABE\u0A9A\u0ABE\u0AB0";
    if (user) {
      let newTitle = "Admin Panel";
      if (currentPage === "dashboard") {
        newTitle = "Dashboard";
      } else if (currentPage === "articles") {
        newTitle = "Manage Articles";
      } else if (currentPage === "users") {
        newTitle = "Manage Users";
      }
      document.title = `${newTitle}${titleSuffix}`;

      let meta = document.head.querySelector('meta[name="robots"]');
      if (!meta) {
        meta = document.createElement("meta");
        meta.name = "robots";
        document.head.appendChild(meta);
      }
      meta.content = "noindex, nofollow";

      return () => {
        document.title = originalTitle;
        const metaTag = document.head.querySelector('meta[name="robots"]');
        if (metaTag) {
          document.head.removeChild(metaTag);
        }
      };
    } else {
      const newTitle = showSignup
        ? "Administrator Sign Up"
        : "Administrator Login";
      document.title = `${newTitle}${titleSuffix}`;

      let meta = document.head.querySelector('meta[name="robots"]');
      if (!meta) {
        meta = document.createElement("meta");
        meta.name = "robots";
        document.head.appendChild(meta);
      }
      meta.content = "noindex, nofollow";
    }
  }, [user, currentPage, originalTitle, showSignup]);

  const handleLogin = (loggedInUser) => {
    localStorage.setItem("adminUser", JSON.stringify(loggedInUser));
    setUser(loggedInUser);
  };

  if (isAuthLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    if (showSignup) {
      return <Signup onSwitch={() => setShowSignup(false)} />;
    }
    return (
      <Login
        onLogin={handleLogin}
        setNotification={setNotification}
        onSwitch={() => setShowSignup(true)}
      />
    );
  }

  const renderPage = () => {
    if (user.isAdmin === true) {
      switch (currentPage) {
        case "dashboard":
          return (
            <Dashboard
              stats={stats}
              articles={articles.slice(0, 5)}
              allUsers={allUsers}
            />
          );
        case "articles":
          return (
            <CrudManager
              title="Articles"
              items={articles}
              setItems={setArticles}
              user={user}
              icon={<ArticleIcon />}
              allUsers={allUsers}
              setNotification={setNotification}
            />
          );
        case "users":
          return (
            <Users
              allUsers={allUsers}
              currentUser={user}
              setNotification={setNotification}
            />
          );
        default:
          return (
            <Dashboard
              stats={stats}
              articles={articles.slice(0, 5)}
              allUsers={allUsers}
            />
          );
      }
    }
    return <p>Access Denied. You do not have administrator privileges.</p>;
  };

  return (
    <div className="flex h-screen bg-gray-100 flex-col md:flex-row">
      {notification && (
        <div
          className={`fixed top-4 right-4 left-4 sm:left-auto sm:w-auto max-w-sm z-[100] p-4 rounded-lg shadow-lg text-white ${
            notification.type === "success" ? "bg-green-500" : "bg-red-500"
          }`}
        >
          {notification.message}
        </div>
      )}
      <Sidebar
        user={user}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        onLogout={handleLogout}
      />
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        {renderPage()}
      </main>
    </div>
  );
}
